// ─── Orts-Parser: Stadt und Land aus Freitext ableiten ──────────────────────
//
// Geburts-/Sterbeorte werden als Freitext erfasst – mal „Berlin", mal
// „Berlin, Deutschland", mal nur „Deutschland". Diese Helfer erkennen anhand
// einer Liste bekannter Ländernamen, welches Segment das Land ist, und trennen
// Stadt/Region davon. So funktioniert die Länder-Auswertung auch, wenn nur das
// Land bekannt ist – und ganz ohne externes Geocoding.
//
// Wird von Backend (Statistik) und Frontend (Personen-Panel) genutzt.

// Kanonischer deutscher Name → Aliase (englisch, umlautfrei, Kürzel).
// Fokus auf Europa + weltweit häufige Länder; historische Staaten bewusst
// ausgeklammert (kollidieren mit Regionen). Bei Bedarf erweiterbar.
const COUNTRY_DEFS: Array<[string, ...string[]]> = [
  ['Deutschland', 'germany', 'deutschld', 'brd', 'de', 'ger'],
  ['Österreich', 'osterreich', 'oesterreich', 'austria', 'at'],
  ['Schweiz', 'switzerland', 'suisse', 'svizzera', 'ch'],
  ['Frankreich', 'france', 'fr'],
  ['Italien', 'italy', 'italia', 'it'],
  ['Spanien', 'spain', 'espana', 'es'],
  ['Portugal', 'pt'],
  ['Niederlande', 'netherlands', 'holland', 'nederland', 'nl'],
  ['Belgien', 'belgium', 'belgique', 'be'],
  ['Luxemburg', 'luxembourg', 'lu'],
  ['Vereinigtes Königreich', 'united kingdom', 'uk', 'großbritannien', 'grossbritannien', 'great britain', 'england', 'schottland', 'wales', 'gb'],
  ['Irland', 'ireland', 'ie'],
  ['Dänemark', 'denmark', 'danemark', 'dk'],
  ['Norwegen', 'norway', 'no'],
  ['Schweden', 'sweden', 'sverige', 'se'],
  ['Finnland', 'finland', 'suomi', 'fi'],
  ['Island', 'iceland', 'is'],
  ['Polen', 'poland', 'polska', 'pl'],
  ['Tschechien', 'czech republic', 'czechia', 'tschechische republik', 'cz'],
  ['Slowakei', 'slovakia', 'sk'],
  ['Ungarn', 'hungary', 'magyarorszag', 'hu'],
  ['Österreich-Ungarn', 'austria-hungary', 'k.u.k.'],
  ['Rumänien', 'romania', 'ro'],
  ['Bulgarien', 'bulgaria', 'bg'],
  ['Griechenland', 'greece', 'gr'],
  ['Kroatien', 'croatia', 'hr'],
  ['Slowenien', 'slovenia', 'si'],
  ['Serbien', 'serbia', 'rs'],
  ['Bosnien und Herzegowina', 'bosnia', 'bosnien', 'ba'],
  ['Nordmazedonien', 'north macedonia', 'mazedonien', 'mk'],
  ['Albanien', 'albania', 'al'],
  ['Montenegro', 'me'],
  ['Kosovo', 'xk'],
  ['Russland', 'russia', 'russland', 'ru', 'russische föderation'],
  ['Ukraine', 'ua'],
  ['Weißrussland', 'belarus', 'weissrussland', 'by'],
  ['Litauen', 'lithuania', 'lt'],
  ['Lettland', 'latvia', 'lv'],
  ['Estland', 'estonia', 'ee'],
  ['Moldau', 'moldova', 'moldawien', 'md'],
  ['Türkei', 'turkey', 'turkiye', 'tr'],
  ['Zypern', 'cyprus', 'cy'],
  ['Malta', 'mt'],
  ['Vereinigte Staaten', 'usa', 'united states', 'united states of america', 'us', 'amerika', 'vereinigte staaten von amerika'],
  ['Kanada', 'canada', 'ca'],
  ['Mexiko', 'mexico', 'mx'],
  ['Brasilien', 'brazil', 'brasil', 'br'],
  ['Argentinien', 'argentina', 'ar'],
  ['Chile', 'cl'],
  ['Kolumbien', 'colombia', 'co'],
  ['Peru', 'pe'],
  ['Australien', 'australia', 'au'],
  ['Neuseeland', 'new zealand', 'nz'],
  ['China', 'cn', 'volksrepublik china'],
  ['Japan', 'jp'],
  ['Indien', 'india', 'in'],
  ['Südkorea', 'south korea', 'korea', 'sudkorea', 'kr'],
  ['Israel', 'il'],
  ['Südafrika', 'south africa', 'sudafrika', 'za'],
  ['Ägypten', 'egypt', 'agypten', 'eg'],
  ['Marokko', 'morocco', 'ma'],
];

// Normalisiert ein Segment für den Vergleich (klein, ohne Mehrfach-Leerzeichen).
function normalizeSegment(segment: string): string {
  return segment.trim().toLowerCase().replace(/\s+/g, ' ').replace(/\.$/, '');
}

// Alias (normalisiert) → kanonischer Name.
const COUNTRY_LOOKUP: Map<string, string> = (() => {
  const map = new Map<string, string>();
  for (const [canonical, ...aliases] of COUNTRY_DEFS) {
    map.set(normalizeSegment(canonical), canonical);
    for (const alias of aliases) map.set(normalizeSegment(alias), canonical);
  }
  return map;
})();

/** Ist dieses Ortssegment ein bekanntes Land? → kanonischer Name oder null. */
function matchCountry(segment: string): string | null {
  return COUNTRY_LOOKUP.get(normalizeSegment(segment)) ?? null;
}

/**
 * Leitet das Land aus einem Ortstext ab. Prüft die Komma-Segmente von hinten
 * (das Land steht meist am Ende) und erkennt auch reine Länder-Einträge
 * („Deutschland"). Gibt den kanonischen Ländernamen zurück oder null.
 */
export function deriveCountry(place: string | null | undefined): string | null {
  if (!place) return null;
  const parts = place.split(',').map((s) => s.trim()).filter(Boolean);
  for (let i = parts.length - 1; i >= 0; i--) {
    const country = matchCountry(parts[i]!);
    if (country) return country;
  }
  return null;
}

/**
 * Leitet die Stadt/Region aus einem Ortstext ab: alle Segmente ohne das
 * erkannte Land. Gibt null zurück, wenn nur ein Land bekannt ist („Deutschland"
 * → keine Stadt) oder der Text leer ist.
 */
export function deriveCity(place: string | null | undefined): string | null {
  if (!place) return null;
  const parts = place.split(',').map((s) => s.trim()).filter(Boolean);
  const cityParts = parts.filter((p) => !matchCountry(p));
  if (cityParts.length === 0) return null;
  return cityParts.join(', ');
}

/** Liste aller bekannten kanonischen Ländernamen (z. B. für Auswahlhilfen). */
export const KNOWN_COUNTRIES: string[] = COUNTRY_DEFS.map(([canonical]) => canonical);

// ─── Ortstexte deuten ────────────────────────────────────────────────────────
//
// Ein Ortsfeld ist Freitext, und Freitext aus Ahnenforschung ist besonders
// bunt: „Erfurt", „Erfurt, Thüringen, Deutschland", „Escherndorf / Main",
// „unbekannt", „?". Bevor irgendetwas nachgeschlagen wird, muss klar sein, WAS
// nachgeschlagen wird – und ob überhaupt.
//
// Steht hier in shared, weil beide Seiten dieselbe Antwort brauchen: Der
// Server legt Orte unter ihrem Schlüssel ab, der Browser sucht sie unter
// demselben Schlüssel wieder. Zwei Normalisierungen, die auseinanderlaufen,
// hießen: die Karte findet die Koordinaten nicht, die der Server längst hat.

/**
 * Texte, die keinen Ort meinen.
 *
 * Sie kommen häufig vor, und jeder einzelne kostete bisher eine Anfrage an
 * einen fremden Dienst, die niemals etwas Sinnvolles liefern konnte.
 */
const KEIN_ORT = new Set([
  'unbekannt', 'unbekannter ort', 'unknown', 'nn', 'n.n.', 'n. n.',
  'ka', 'k.a.', 'k. a.', 'ohne', 'ohne ort', 'keine angabe', 'unbek', 'unbek.',
  'weiß nicht', 'weiss nicht', 'unbestimmt',
]);

/** Nur Satzzeichen, Striche, Punkte – „???", „-", „…". */
const NUR_ZEICHEN = /^[\s\p{P}\p{S}]*$/u;

/**
 * Meint dieser Text gar keinen Ort?
 *
 * Wichtig genug für eine eigene Frage: Solche Einträge dürfen nicht als
 * „konnte nicht zugeordnet werden" in der Korrekturliste stehen. Sie SIND
 * nicht zuzuordnen, und wer sie dort sieht, sucht einen Fehler, den es nicht
 * gibt.
 */
export function istKeinOrt(text: string | null | undefined): boolean {
  if (!text) return true;
  const sauber = text.trim();
  if (sauber === '') return true;
  if (NUR_ZEICHEN.test(sauber)) return true;
  return KEIN_ORT.has(sauber.toLowerCase());
}

/**
 * Der Schlüssel, unter dem ein Ort im Wörterbuch steht.
 *
 * Zusammengeführt wird, was offensichtlich dasselbe meint: Groß- und
 * Kleinschreibung, doppelte Leerzeichen, Schrägstriche als Trenner, ein Komma
 * am Ende. NICHT zusammengeführt wird alles Weitergehende – „Erfurt" und
 * „Erfurt, Thüringen" bleiben getrennt, weil nur der Nachschlagedienst
 * entscheiden kann, ob das derselbe Ort ist.
 */
export function ortsSchluessel(text: string): string {
  return text
    .replace(/\s*\/\s*/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/\s*,\s*/g, ', ')
    .replace(/[,\s]+$/, '')
    .replace(/^[,\s]+/, '')
    .trim()
    .toLowerCase();
}

/** Die Suchanfrage für den Nachschlagedienst – wie der Schlüssel, aber lesbar. */
export function suchtext(text: string): string {
  return text
    .replace(/\s*\/\s*/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/\s*,\s*/g, ', ')
    .replace(/[,\s]+$/, '')
    .replace(/^[,\s]+/, '')
    .trim();
}
